import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { SISCard, SISCallout, SISInfluence, SISTakeaway, SISNoteImage } from "@/components/sis/SISLessonShell";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";

export default function SouthIndian8() {
  const { lesson, course, accent } = findLesson("south-indian-songbook", "8");
  return (
    <LessonShell
      course={course}
      courseSlug="south-indian-songbook"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <YouTubeEmbed
        videoId="43Et7noMM8c"
        title="Azhagu Kutti Chellam (Reprise) — Ved Shanker Sugavanam · sung by Shakthisree Gopalan"
        caption="Listen to the opening: the bass holds A while the chords above it change — A, E/A, D/A. That stillness sets the mood before the harmony starts to travel."
      />

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/08-azhagu-kutti-chellam-page1.png"
        caption="Jason's chart — the opening A · E/A · D/A, the seven chords of A major (A Bm C♯m D E F♯m G♯°), and C♯m/A, which he names as Amaj7."
      />

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/08-azhagu-kutti-chellam-page2.png"
        caption="Page 2 — the 6/8 groove: A(add2), E/A, Bm7, E7 written out note by note over an A bass, plus the 'mediant' move (C♯sus4 → C♯7 → F♯m) and Bm7 → E7."
      />

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/08-azhagu-kutti-chellam-page3.png"
        caption="Page 3 — the song's sections: A D/A A Bm7 E7; the relative minor (F♯m C♯7 Bm7 E7, with Bm9 and D/E); a section a minor 3rd up (C G/B Am E7 → A); and the nylon-guitar solo."
      />

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/08-azhagu-kutti-chellam-page4.png"
        caption="Page 4 — more sections: Amaj7 Bm7 C♯m7 Em7 Dmaj7 D/E, a B7 vamp (marked B Mixolydian ♭6), and an ending with Am, Am6 and E/A."
      />

      <SISCard label="The big idea" title="A held bass under moving chords — the pedal point">
        <p>
          The opening of <em>Azhagu Kutti Chellam (Reprise)</em> rests on one compositional decision: <strong>the bass note A stays put</strong> while the chords above it change — A, then E/A, then D/A. The bass sits on A like a steady arm while the harmony shifts on top. (The rest of the song does move: Jason's chart goes on to Bm7, E7, F♯m, C♯7 and more.)
        </p>
        <p>
          This technique is called a <strong>pedal point</strong>: one voice — usually the bass, usually the tonic — holds a note while the harmony above it shifts. The result is a sense of stillness and safety. The world can change; the foundation does not.
        </p>
        <p>
          Jason spells out what E/A means at the keyboard: <em>"So you'll be playing E G sharp B in the treble, in the top, and you'll be playing A in the left hand."</em> That is the whole technique: the left hand holds A while the right hand changes chord.
        </p>
      </SISCard>

      <RomanNumeralAnalysis
        label="Over the A pedal · A major"
        caption="Every chord here has A in the bass. D/A creates an ambiguity: the harmony says IV (the subdominant), but the bass insists on the tonic. C♯m over A spells A–C♯–E–G♯ — an Amaj7, as Jason notes on his chart. (Numerals name the chord above the pedal.)"
        chords={[
          { chord: "A", rn: "I", function: "Tonic", pitches: ["A3","C#4","E4","A4"] },
          { chord: "E/A", rn: "V (A pedal)", function: "Dominant", pitches: ["A3","E4","G#4","B4"] },
          { bar: true },
          { chord: "D/A", rn: "IV (A pedal)", function: "Pre-dominant", pitches: ["A3","D4","F#4","A4"] },
          { chord: "A", rn: "I", function: "Tonic", pitches: ["A3","C#4","E4","A4"] },
          { bar: true },
          { chord: "C♯m/A", rn: "iii (A pedal)", function: "Mediant", pitches: ["A3","C#4","E4","G#4"] },
          { chord: "E/A", rn: "V (A pedal)", function: "Dominant", pitches: ["A3","E4","G#4","B4"] },
        ]}
      />

      <SISCard label="Slash chords decoded" title="D/A — when harmony and bass disagree">
        <p>
          The notation D/A means: play a D major chord <em>over</em> a bass note of A. The D major chord contains D, F♯, and A. Add the bass A below that and you get A–D–F♯–A — a D major chord in second inversion (its 5th, A, in the bass). Because that A is also the key note, the chord sounds like D floating over home.
        </p>
        <p>
          The slash chord creates productive ambiguity: the upper harmony leans toward the subdominant (IV), while the bass anchors to the tonic (I). Organists have used the same idea for centuries — the pedal holds a note while the upper voices wander — and the tension releases when the bass finally moves.
        </p>
        <p>
          The C♯m/A chord works similarly: C♯–E–G♯ over A = A–C♯–E–G♯, which is an Amaj7 chord. The composer gives you the colour of a major seventh chord (sophisticated, open, floating) using the grammatically simpler spelling of a minor chord over the pedal.
        </p>
      </SISCard>

      <SISCard label="Right-hand voice leading" title="How the melody moves when the bass stands still">
        <p>
          When the bass is frozen, the upper voices carry all the movement — and movement by step (semitone or whole tone) is more powerful than movement by leap. With the voicings in the chord boxes above, the top note of each chord moves:
        </p>
        <ul className="mt-2 space-y-1 pl-4 text-[15px]">
          <li><strong>A (root of A)</strong> — the top voice starts on A</li>
          <li><strong>B (5th of E/A)</strong> — moves up one step to B</li>
          <li><strong>A (5th of D/A)</strong> — steps back down to A</li>
          <li><strong>G♯ (5th of C♯m — the major 7th over the A bass)</strong> — a half-step lower, on G♯</li>
        </ul>
        <p className="mt-2">
          That A → B → A → G♯ line on top rises and then gently descends. This is the melody's emotion, embedded in the harmony itself — doing less in the bass lets the upper voices say more.
        </p>
      </SISCard>

      <SISInfluence
        artists={[
          "Ved Shanker Sugavanam — Azhagu Kutti Chellam soundtrack",
          "Shakthisree Gopalan (vocals, Reprise)",
          "Keba Jeremiah (guitar, Reprise)",
        ]}
      >
        <p>
          Compare the Reprise with the film's main version of the song (sung by Balram) to hear how much the arrangement changes the mood. In the Reprise, listen for Keba Jeremiah's guitar — Jason's chart marks a nylon-guitar solo section.
        </p>
      </SISInfluence>

      <SISCallout label="Practice drill — the LH thumb anchor">
        <p>
          Place your left hand on a low A. Do not move it. With your right hand, play A major (A–C♯–E) → E major (E–G♯–B) → D major (D–F♯–A) → C♯ minor (C♯–E–G♯). Feel how the constant A bass ties all four chords into one family. Now try the same with a bass of D instead of A — notice how the mood shifts entirely. The bass note is the emotional root of the whole passage.
        </p>
      </SISCallout>

      <SISTakeaway>
        <p>
          The pedal point is one of the most powerful and underused tools in a songwriter's vocabulary. It resolves the tension between harmonic movement (which creates interest) and harmonic stability (which creates comfort): let the bass provide the stability, and let the upper voices move freely. The audience gets both at once. In Carnatic music, the tanpura drone plays a similar role — a held tonic (with its fifth) against which all the melodic colour floats. On the piano, a held left-hand bass note can do the same job.
        </p>
      </SISTakeaway>
    </LessonShell>
  );
}
